import { createContext, useContext, useReducer } from 'react'
import { RemoteRecordingStatus, WaitlistItem, WaitlistStatus } from './pages/CollaboratePage/Types'
import { logger } from './services/Logger'
import { MuseMidiDeviceInfo } from './services/MusePeer'
import { RemoteTrack, Room as LiveKitRoom, LocalTrack } from 'livekit-client'
import { smartStringify } from './utils'
import { MusePeer, PeerManifest } from './services/RoomContextMusePeer'
import { RoomScheduledRule, ScreenRecordingInfo } from './AppContext'
import FakeMediaStreamTrack from './core/AudioEngine/FakeMediaStreamTrack'
import {
    AudioInputStreamInfo,
    AudioTrackWithInfo,
    ParentRoomFragment,
    SfuNode,
    SubRoomFragment,
    UserProfileWithUrn,
} from './UserContext'
import { IAudioEngineRoom } from './core/AudioEngine'
import { PerfStatsEmitter } from './pages/PerfStatsPage/PerfStatsEmitter'
import { SettingsLocation, SettingsTab } from './components/SessionSettings/SessionSettings'
import { TFunction } from 'i18next'

const log = logger.createChildLogger({ name: 'RoomContext' })

export type MuseRoomNetworkTopologyTypes = 'p2p' | 'sfu'

export type RemoteDesktopControlStatus = {
    reqId: number
    sessionId: string
    username: string
    status: 'allowed' | 'denied' | 'revoked' | ''
}

export type AudioEngineIncompatiblePeerWarningDialogData = {
    isOpen: boolean
    doNotShowAgain: boolean
    incompatiblePeers: { username: string; appVersion: string }[]
    audioengineLabel: string
}

export type MidiStats = {
    txCount: number
    rxCount: number
}

export type RoomTierPolicy = {
    id: string
    maxUserCount: number
    maxAudioDeviceCount: number
    maxMidiDeviceCount: number
    sessionDurationLimitMins: number
    allowAudioEngineTx: boolean
    allowMultipleSameUserInRoom: boolean
    allowSubrooms: boolean
    allowAudienceChat: boolean
}

export type RoomSessionState = {
    isBoosted: boolean
    realSessionStartTime: Date | null
    sessionLimitEndTime: Date | null

    // string versions of the above dates, for comparison purposes.
    realSessionStartTimeStr: string | null
    sessionLimitEndTimeStr: string | null
}

export function toSessionState(doc: any): RoomSessionState {
    const s = {
        isBoosted: doc.isBoosted ?? false,
        realSessionStartTime: doc.realSessionStartTime ? new Date(doc.realSessionStartTime) : null,
        realSessionStartTimeStr: doc.realSessionStartTime,
        sessionLimitEndTime: doc.sessionLimitEndTime ? new Date(doc.sessionLimitEndTime) : null,
        sessionLimitEndTimeStr: doc.sessionLimitEndTime,
        startTime: doc.startTime ? new Date(doc.startTime) : null,
    }

    return s
}

export function isSessionStateEqual(a: RoomSessionState, b: RoomSessionState): boolean {
    return (
        a.isBoosted === b.isBoosted &&
        a.realSessionStartTimeStr === b.realSessionStartTimeStr &&
        a.sessionLimitEndTimeStr === b.sessionLimitEndTimeStr
    )
}

export type ApiRoomDocument = {
    _id: string
    name: string
    users: string[]
    allowUserAutoJoin: boolean
    defaultNetworkTopology: string
    isBoosted: boolean
    realSessionStartTime: string | null
    sessionLimitEndTime: string | null
    suppressSessionFeedback: boolean
    allParticipantsJoinMuted: boolean
    hostUrn: string
    joinCode: string
    fileStorageSize: number
    createdFromAppVersion: string
    lastActiveTime: string | null
    allowGuests: boolean
    permanent: boolean
    resetAfterSession: boolean
    isSubroomsEnabled: boolean
    isListeningSession: boolean
    roomTierPolicy: RoomTierPolicy
    deferRoomGCUntil: string
    isLivekitCloudSfu: boolean
    createdAt: string
    updatedAt: string
    customJoinCode: string | null
    host: UserProfileWithUrn // { userUrn: string, username: string }
    joinLink: string
    scheduledStartTime?: string
    scheduledEndTime?: string
    password?: string | null
    anonymousId?: string | null
    parentRoomUrn?: string | null
    parentRoom?: ParentRoomFragment | null
    subrooms?: SubRoomFragment[]
    isConferenceMode: boolean
    autoRecordSessions: boolean
    org?: string
    apiCreated?: boolean
    assignedUsers?: string[]
    isUserSubroomAssignmentActive?: boolean
    isUserSubroomAutoAssigned?: boolean
}

export type ApiRoomRequest = {
    // if specified, update the specified room.
    roomUrn?: string

    // the URN of the parent room; set if is a subroom.
    parentRoomUrn?: string

    // the room name, used only for display purposes
    name: string

    // scheduled room info; null if not a scheduled room.
    scheduledStartTime?: Date
    scheduledEndTime?: Date
    scheduledTimeZone?: string // IANA code; not used for now, only stored.
    scheduledRrule?: RoomScheduledRule | null
    calendarIntegrationSelection?: 'google' | ''

    cloudRecordingEnabled?: boolean

    collaborators: string[]
    allowJoiningFromCode: boolean
    password?: string
    createdFromAppVersion?: string // added on app version v1.5.0

    permanent?: boolean
    isSubroomsEnabled?: boolean
    isListeningSession?: boolean
    resetAfterSession?: boolean
    allowUserAutoJoin?: boolean
    allParticipantsJoinMuted?: boolean
    customJoinCode?: string

    isUserSubroomAssignmentActive?: boolean
    isUserSubroomAutoAssigned?: boolean
}

export type ApiCreateRoomResponse = {
    room: ApiRoomDocument
    collaboratorsNotFound: string[]
    googleCalendarLink: string | null
}

export type ApiGetRoomResponse = {
    room: ApiRoomDocument
}

export type MissingRemoteDevice = {
    sessionId: string
    username: string
    deviceId: string
    displayLabel: string
    deviceType: 'audio' | 'video'
    addedTime: Date
    logged?: boolean
}

export type RoomContextState = {
    roomUrn: string
    sessionId: string
    preRoomUrn: string
    joinCode: string | null
    joinLink: string | null
    customJoinCode: string | null
    customJoinLink: string | null
    anonymousId: string
    password: string | null
    name: string
    host: UserProfileWithUrn | null
    sessionStartedTime: Date | null
    permanent: boolean | null
    resetAfterSession: boolean | null
    isSubroomsEnabled: boolean
    isListeningSession: boolean
    autoRecordSessions: boolean
    allowUserAutoJoin: boolean
    suppressSessionFeedback: boolean
    joinLinkMetadata: { [key: string]: string } | null
    org: string | null
    parentRoomUrn: string | null
    parentRoom: ParentRoomFragment | null
    subrooms: SubRoomFragment[]
    assignedUsers: string[]
    isUserSubroomAssignmentActive: boolean
    isUserSubroomAutoAssigned: boolean

    myWaitlistStatus: WaitlistStatus
    roomWaitlist: WaitlistItem[]
    peers: { [key: string]: MusePeer }
    peerUserProfileCache: {
        [key: string /*userUrn*/]: { userUrn: string; username: string; displayName: string; isGuest: boolean }
    }
    isHost: boolean
    pinnedPeerId: string | null
    pinKind: PinKind
    expectedNetworkTopology: MuseRoomNetworkTopologyTypes | ''
    isSwitchingRoom: string | null
    videoHiddenSessionIds: string[]
    isMyAudioDeprioritized: boolean
    allParticipantsJoinMuted: boolean

    // room policy
    roomTierPolicy: RoomTierPolicy
    sessionState: RoomSessionState

    // internal audio streams
    currPeerAudioOutputStream: MediaStream | null
    currPeerAudioOutputStreamForScreenRecording: MediaStream | null
    localLoopbackAudioLivekitTrack: LocalTrack | null

    myVideoMuteState: boolean
    myAudioMuteState: { [key: string]: boolean }
    myAudioTalkbackState: { [key: string]: boolean }
    myMidiMuteState: { [key: string]: boolean }
    isTalkbackKeyPressed: boolean
    // Tracks which devices have auto-mute enabled
    autoMuteEnabled: Record<string, boolean>
    // Indicates which streams are triggering the auto-mute threshold
    autoMuteTriggeringStreams: Record<string, boolean>

    remoteRecordingStatus: RemoteRecordingStatus

    // used to force cause manifest to be sent to peers
    localManifestV: number

    isScreenRecording: ScreenRecordingInfo | null
    screenRecordingCommand: { reqId: number; command: 'start' | 'stop'; openSettingsOnStreamError?: boolean } | null

    // window states
    /** @deprecated State is now held in session ViewMode#sidebarRetracted **/
    isSidebarRetracted: boolean
    isSidebarDetached: boolean
    // Indicates of the main video gallery is hidden
    /** @deprecated State is now held in session ViewMode#galleryHidden **/
    isRetracted: boolean
    isRetractedPrev: boolean
    isRetractedDoNotRestoreSavedWindowDimension: boolean // must be set along with isRetracted.
    isRetractedInProgress: boolean
    /** @deprecated This state is now held in session ViewMode#alwaysOnTop **/
    isWindowSticky: boolean
    wasWindowStickyBeforeScreenShare: boolean
    isReorderVideoMode: boolean
    /** @deprecated Use ViewMode#videoOrder instead **/
    galleryClientOrdering: string[] | null

    isSettingModalOpen: SettingsLocation | null
    isShowingWaitingRoomPopup: boolean
    isForceShowingWaitingRoomPopup: boolean
    isCannotPlayAudioDialogOpen: boolean

    // dialogs
    isRoomWaitlistDialogOpen: boolean
    isRecordTalkbackKeyDialogOpen: boolean
    isLeaveConfirmationDialogOpen: { uuid: string; endSession: boolean } | null
    isRemoteDesktopControlQueryPromptOpen: { sessionId: string; username: string } | null
    isRemoteDesktopControlRequestPromptOpen: { reqId: number; fromSessionId: string; fromUsername: string } | null
    isRemoveUserConfirmationDialogOpen: { sessionId: string; username: string } | null
    audioEngineIncompatiblePeerWarningDialog: AudioEngineIncompatiblePeerWarningDialogData
    isRoomAppUpgradeRequiredDialogOpen: { type: 'outdated_room' | 'outdated_peer'; username?: string } | null

    // device stats
    remoteDesktopControlStatus: {
        reqId: number
        sessionId: string
        username: string
        status: 'allowed' | 'denied' | 'revoked' | ''
    } | null

    // not used for now
    missingRemoteAudioDevices: MissingRemoteDevice[]
    missingRemoteVideoDevices: MissingRemoteDevice[]

    // misc. room states
    accordionOpenStates: { [key: string /*sessionId*/]: boolean } // "false" is accordion open.
    isShowingGalleryQuickSetupGuide: boolean
    isExiting: boolean
    isBoostBeingApplied: boolean

    // LiveKit stuff
    sfuRoom: LiveKitRoom | null
    sfuRoomConnected: boolean
    publishedSfuAudios: AudioTrackWithInfo[]
    sfuRoomInfo: { nearestNode: SfuNode } | null
    publishedSfuMidi: MuseMidiDeviceInfo[]

    // Audio Engine
    audioEngineRoom: IAudioEngineRoom | null

    // screen recording
    screenControlRequest: { reqId: number; receiverSessionId: string; op: '' | 'start' | 'stop' }
    lastScreenControlRequestResponse: {
        reqId: number
        requesterSessionId: string
        status: 'allowed' | 'denied' | 'revoked' | ''
    }
    screenControlRequestResponseHistory: {
        reqId: number
        requesterSessionId: string
        status: 'allowed' | 'denied' | 'revoked' | ''
    }[]

    // misc. states
    // set in useRoomActions:joinRoom() to mark when the "join-room" ws api call was made;
    // used in RoomController to determine "join-room" timeout.
    joinRoomCallStartedTime: Date | null

    isLeaving: boolean
}

export const initialRoomContext: RoomContextState = {
    roomUrn: '',
    sessionId: '',
    preRoomUrn: '',
    joinCode: null,
    joinLink: null,
    customJoinCode: null,
    customJoinLink: null,
    anonymousId: '',
    password: null,
    name: '',
    host: null,
    sessionStartedTime: null,
    permanent: null,
    resetAfterSession: null,
    isSubroomsEnabled: false,
    isListeningSession: false,
    autoRecordSessions: false,
    allowUserAutoJoin: false,
    suppressSessionFeedback: false,
    joinLinkMetadata: null,
    org: null,
    parentRoomUrn: null,
    parentRoom: null,
    subrooms: [],
    assignedUsers: [],
    isUserSubroomAssignmentActive: false,
    isUserSubroomAutoAssigned: false,

    myWaitlistStatus: 'isJoining',
    roomWaitlist: [],
    peers: {},
    peerUserProfileCache: {},
    isHost: false,
    pinnedPeerId: null,
    pinKind: 'camera',
    expectedNetworkTopology: '',
    isSwitchingRoom: null,
    videoHiddenSessionIds: [],
    isMyAudioDeprioritized: false,
    allParticipantsJoinMuted: false,

    // reasonable defaults for when the roomTierPolicy is not yet loaded --
    // e.g. the settings page for guest users on browser sessions.
    roomTierPolicy: {
        id: '',
        maxUserCount: 3,
        maxAudioDeviceCount: 2,
        maxMidiDeviceCount: 1,
        sessionDurationLimitMins: 60,
        allowAudioEngineTx: false,
        allowMultipleSameUserInRoom: false,
        allowSubrooms: false,
        allowAudienceChat: false,
    },

    sessionState: {
        isBoosted: false,
        realSessionStartTime: null,
        sessionLimitEndTime: null,
        realSessionStartTimeStr: null,
        sessionLimitEndTimeStr: null,
    },

    currPeerAudioOutputStream: null,
    currPeerAudioOutputStreamForScreenRecording: null,
    localLoopbackAudioLivekitTrack: null,

    myVideoMuteState: false,
    myAudioMuteState: {},
    myAudioTalkbackState: {},
    autoMuteEnabled: {},
    autoMuteTriggeringStreams: {},
    myMidiMuteState: {},
    isTalkbackKeyPressed: false,

    remoteRecordingStatus: {
        isBeingRecorded: false,
    },

    localManifestV: 0,

    isScreenRecording: null,
    screenRecordingCommand: null,

    isSidebarRetracted: false,
    isSidebarDetached: false,
    isRetracted: false,
    isRetractedPrev: false,
    isRetractedDoNotRestoreSavedWindowDimension: false,
    isRetractedInProgress: false,
    isWindowSticky: false,
    wasWindowStickyBeforeScreenShare: false,
    isReorderVideoMode: false,
    galleryClientOrdering: null,

    isSettingModalOpen: null,
    isShowingWaitingRoomPopup: false,
    isForceShowingWaitingRoomPopup: false,
    isCannotPlayAudioDialogOpen: false,
    isRoomWaitlistDialogOpen: false,
    isRecordTalkbackKeyDialogOpen: false,
    isLeaveConfirmationDialogOpen: null,
    isRemoteDesktopControlQueryPromptOpen: null,
    isRemoteDesktopControlRequestPromptOpen: null,
    isRemoveUserConfirmationDialogOpen: null,
    audioEngineIncompatiblePeerWarningDialog: {
        isOpen: false,
        doNotShowAgain: false,
        incompatiblePeers: [],
        audioengineLabel: '',
    },
    isRoomAppUpgradeRequiredDialogOpen: null,

    remoteDesktopControlStatus: null,

    missingRemoteAudioDevices: [],
    missingRemoteVideoDevices: [],

    accordionOpenStates: {},
    isShowingGalleryQuickSetupGuide: false,
    isExiting: false,
    isBoostBeingApplied: false,

    sfuRoom: null,
    sfuRoomConnected: false,
    sfuRoomInfo: null,

    publishedSfuAudios: [],
    publishedSfuMidi: [],

    audioEngineRoom: null,

    screenControlRequest: { reqId: 0, receiverSessionId: '', op: '' },
    lastScreenControlRequestResponse: { reqId: 0, requesterSessionId: '', status: '' },
    screenControlRequestResponseHistory: [],

    joinRoomCallStartedTime: null,

    isLeaving: false,
}

export function roomWaitlistHasAppSerialId(roomWaitlist: WaitlistItem[], appSerialId: string) {
    return roomWaitlist.filter((item) => item.appSerialId === appSerialId).length > 0
}

// ---- local p2p audio
export type P2pLocalAudioMedia = {
    stream: MediaStream
    track: AudioTrackWithInfo
    filteredTrack: MediaStreamTrack | null
    gainNode: GainNode | null
}

export type P2pLocalAudioManifest = {
    stream: { id: string }
    track: { id: string }
    info: AudioInputStreamInfo
    controlStates: {
        isMuted: boolean // legacy
        mutedChannelGroups: number[][] // channel groups (number[])
    }
}

// ---- local sfu audio
export type SfuLocalAudioMedia = {
    stream: MediaStream
    track: AudioTrackWithInfo
}

export type SfuLocalAudioManifest = {
    info: AudioInputStreamInfo
    controlStates: {
        isMuted: boolean // legacy
        mutedChannelGroups: number[][] // channel groups (number[])
    }
}

// ---- local p2p video
export type P2pLocalVideoMedia = {
    stream: MediaStream
    track: MediaStreamTrack
}

export type P2pLocalVideoManifest = {
    stream: { id: string }
    controlStates: { isMuted: boolean }
}

// ---- local sfu video
export type SfuLocalVideoManifest = {
    controlStates: { isMuted: boolean }
}

// ---- local p2p desktop
export type LocalDesktopManifestInfo = {
    appVersion: string | null
    // null if shared desktop stream is that of an application,
    // not a physical monitor display.
    screenIndex: number | null
    scaleFactor: number | null
    screenWidth: number | null
    screenHeight: number | null
    clientKind: 'electron-app' | 'browser'
}
export type LocalDesktopManifest = {
    stream: { id: string }
    info: LocalDesktopManifestInfo
}

// ---- remote p2p media
export type P2pRemoteMedia = {
    readonly fromTopology: 'p2p'
    stream: MediaStream
    track: MediaStreamTrack
}

// ---- remote sfu media
export type SfuRemoteMedia = RemoteTrack & {
    readonly fromTopology: 'sfu'
    label: string
    deviceId: string
    stream: MediaStream
    track: MediaStreamTrack
}

export type P2pRemoteAudio = P2pRemoteMedia & {
    info: AudioInputStreamInfo
}

export type SfuRemoteAudio = SfuRemoteMedia & {
    info: AudioInputStreamInfo
}

export type AudioEngineRemoteAudio = {
    readonly fromTopology: 'ae'
    track: FakeMediaStreamTrack
    info: AudioInputStreamInfo
}

type RoomContextActionBase = {
    caller?: string
    reason?: string
}

export type RoomBaseInfo = {
    roomUrn: string
    sessionId: string
    preRoomUrn: string
    joinCode: string | null
    joinLink: string | null
    customJoinCode: string | null
    customJoinLink: string | null
    anonymousId: string
    password: string | null
    name: string
    host: UserProfileWithUrn | null
    sessionStartedTime: Date | null
    permanent: boolean | null
    resetAfterSession: boolean | null
    isSubroomsEnabled: boolean
    isListeningSession: boolean
    allParticipantsJoinMuted: boolean
    autoRecordSessions: boolean
    allowUserAutoJoin: boolean
    parentRoomUrn: string | null
    parentRoom: ParentRoomFragment | null
    subrooms: SubRoomFragment[]
    isConferenceMode: boolean
    suppressSessionFeedback: boolean
    joinLinkMetadata: { [key: string]: string } | null
    org: string | null
    roomTierPolicy: RoomTierPolicy
    assignedUsers: string[]
    isUserSubroomAssignmentActive: boolean
    isUserSubroomAutoAssigned: boolean
}

export type UpdateRoomContext = {
    readonly type: 'update_room_context'
    readonly value: Partial<RoomContextState>
}

export type SetRoomBaseInfo = {
    readonly type: 'set_room_base_info'
    readonly value: RoomBaseInfo
}

export type UpsertPeers = {
    readonly type: 'upsert_peers'
    readonly value: { [key: string /*sessionId*/]: MusePeer }
}

export type UpdatePeer = {
    readonly type: 'update_peer'
    readonly value:
        | {
              sessionId: string
              peer: Partial<MusePeer>
          }
        | {
              sessionId: string
              peer: Partial<MusePeer>
          }[]
}

export type UpdatePeerManifest = {
    readonly type: 'update_peer_manifest'
    readonly value:
        | {
              sessionId: string
              partialManifest: Partial<PeerManifest>
          }
        | {
              sessionId: string
              partialManifest: Partial<PeerManifest>
          }[]
}

export type AddTrackToCurrPeerAudioOutputStream = {
    readonly type: 'add_track_to_currPeerAudioOutputStream'
    readonly value: MediaStreamTrack
}

export type PinKind = 'camera' | 'screen'

export type PinPeerVideo = {
    readonly type: 'pin_peer_video'
    readonly value: {
        pinnedPeerId: string | null // The key in the peers array
        pinKind: PinKind
    }
}

export type RoomContextActions = RoomContextActionBase &
    (
        | UpdateRoomContext
        | SetRoomBaseInfo
        | UpsertPeers
        | UpdatePeer
        | UpdatePeerManifest
        | AddTrackToCurrPeerAudioOutputStream
        | PinPeerVideo
    )

export const loadInitialRoomContext = () => {
    return initialRoomContext
}

let reduceCt = 0
function roomContextReducer(state: RoomContextState, action: RoomContextActions): RoomContextState {
    log.verbose(`updateRoomContext`, `type="${action.type}"`)
    let stateNext = state

    switch (action.type) {
        case 'update_room_context': {
            stateNext = { ...state, ...action.value }
            break
        }
        case 'set_room_base_info': {
            stateNext = { ...state, ...action.value }
            break
        }
        case 'upsert_peers': {
            let peersNext = { ...state.peers }
            log.isVerbose() &&
                log.verbose('upsert_peers', 'peersNext before merge:', { peersNext: Object.keys(peersNext) })
            log.isVerbose() && log.verbose('upsert_peers', 'action.value:', { value: Object.keys(action.value) })

            // breaks abstraction, but also update peerUserProfileCache here in this action.
            const peerUserProfileCacheNext = { ...state.peerUserProfileCache }

            Object.keys(action.value).forEach((sessionId) => {
                // set `peersNext[sessionId]`
                peersNext[sessionId] = { ...action.value[sessionId] }

                // set `peerUserProfileCacheNext`
                const p = peersNext[sessionId]
                peerUserProfileCacheNext[p.userUrn] = {
                    userUrn: p.userUrn,
                    username: p.username,
                    displayName: p.displayName || '',
                    isGuest: p.isGuest, // legacy
                }
            })

            log.isVerbose() &&
                log.verbose('upsert_peers', 'peersNext after merge:', { peersNext: Object.keys(peersNext) })

            stateNext = { ...state, peers: peersNext, peerUserProfileCache: peerUserProfileCacheNext }
            break
        }
        case 'update_peer': {
            if (Array.isArray(action.value)) {
                let peersNext = { ...state.peers }

                for (let value of action.value) {
                    let sessionId = value.sessionId
                    let peer = state.peers[sessionId]
                    if (!peer) {
                        continue
                    }

                    peersNext[sessionId] = { ...peer, ...value.peer }
                }

                stateNext = {
                    ...state,
                    peers: peersNext,
                }
            } else {
                const sessionId = action.value.sessionId

                let peer = state.peers[sessionId]
                if (!peer) {
                    log.error(`updateRoomContext:update_peer`, `peer with sessionId="${sessionId}" not found. no-op.`)
                    break
                }

                const peersNext = { ...state.peers }
                peersNext[sessionId] = { ...peer, ...action.value.peer }

                stateNext = {
                    ...state,
                    peers: peersNext,
                }
            }
            break
        }
        case 'update_peer_manifest': {
            let value: { sessionId: string; partialManifest: Partial<PeerManifest> }[] = []
            if (!Array.isArray(action.value)) {
                value = [action.value]
            }

            let peersNext = { ...state.peers }

            for (let { sessionId, partialManifest } of value) {
                let peer = state.peers[sessionId]
                if (!peer) {
                    continue
                }

                let manifestNext = {
                    ...(peer.manifest || {}),
                    ...partialManifest,
                }

                if (JSON.stringify(manifestNext) === JSON.stringify(peer.manifest)) {
                    continue
                }

                peersNext[sessionId] = {
                    ...peer,
                    manifest: manifestNext,
                }
            }

            stateNext = {
                ...state,
                peers: peersNext,
            }
            break
        }
        case 'add_track_to_currPeerAudioOutputStream': {
            const existingTracks = state.currPeerAudioOutputStream
                ? state.currPeerAudioOutputStream.getAudioTracks()
                : []
            const newStream = new MediaStream([...existingTracks, action.value])

            log.isVerbose() &&
                log.verbose(
                    `updateRoomContext:add_track_to_currPeerAudioOutputStream`,
                    `updated currPeerAudioOutputStream with stream id="${newStream.id}". ` +
                        `It now has ${newStream.getAudioTracks().length} tracks.`
                )

            stateNext = {
                ...state,
                currPeerAudioOutputStream: newStream,
            }
            break
        }
        case 'pin_peer_video': {
            stateNext = {
                ...state,
                pinnedPeerId: action.value.pinnedPeerId,
                pinKind: action.value.pinKind,
            }
            break
        }
        default:
            neverReached(action)
            return state
    }

    log.isSilly() &&
        log.silly(`roomContextReducer`, `updateResult`, {
            timestamp: new Date(),
            ct: reduceCt++,
            caller: action.caller || null,
            reason: action.reason || null,
            actionType: action.type,
            actionValue: smartStringify(action.value),
            stateDiff: {},
            stateNext: smartStringify(stateNext),
        })

    PerfStatsEmitter.inc('roomContextDispatchCt')

    return stateNext
}
function neverReached(never: never) {}

export type RoomContextType = {
    state: RoomContextState
    dispatch: React.Dispatch<RoomContextActions>
}
export const RoomContext = createContext<RoomContextType | undefined>(undefined)

export const RoomContextProvider = ({ children }: any) => {
    const [state, dispatch] = useReducer(roomContextReducer, loadInitialRoomContext())
    return <RoomContext.Provider value={{ state, dispatch }}>{children}</RoomContext.Provider>
}

export const useRoomContext = (): [RoomContextState, React.Dispatch<RoomContextActions>] => {
    const context = useContext(RoomContext)

    if (context === null) {
        throw new Error('useRoomContext must be used within a UserContextProvider')
    }
    return [context?.state!, context?.dispatch!]
}

export function getRoomName(room: ApiRoomDocument | RoomContextState): string {
    if (room.name) {
        return room.name
    }

    return `${room.host?.username}'s Room`
}

export function getUsername(peer: MusePeer, t: TFunction<'translation'>): string {
    if (peer.isGuest) {
        return `${peer.displayName} (${t('Guest')})`
    }
    return peer.username
}
